export type AccessRole =
  | 'ADMIN'
  | 'SA'
  | 'CENTRAL_USER'
  | 'FINANCE'
  | 'INSPECTOR'
  | 'CUSTOMER'
  | 'AUTH'
  | 'PUBLIC';

export type AccessInventoryRoute = {
  id: string;
  title: string;
  path: string;
  roles: AccessRole[];
  apiGroups: string[];
  permission?: string;
  note?: string;
  menuVisible?: boolean;
};

export const accessRoleLabels: Record<AccessRole, string> = {
  ADMIN: 'Yönetici',
  SA: 'Sistem Yöneticisi',
  CENTRAL_USER: 'Merkez Kullanıcı',
  FINANCE: 'Finans Kullanıcısı',
  INSPECTOR: 'P.K.M. Uzmanı',
  CUSTOMER: 'İş Ortağı',
  AUTH: 'Giriş Yapan Kullanıcı',
  PUBLIC: 'Herkese Açık',
};

export const accessRoleColors: Record<AccessRole, string> = {
  ADMIN: 'red',
  SA: 'volcano',
  CENTRAL_USER: 'blue',
  FINANCE: 'green',
  INSPECTOR: 'purple',
  CUSTOMER: 'gold',
  AUTH: 'geekblue',
  PUBLIC: 'default',
};

export const accessInventoryRoutes: AccessInventoryRoute[] = [
  {
    id: 'dashboard',
    title: 'Genel Görünüm',
    path: '/',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'CUSTOMER'],
    apiGroups: ['/proposals', '/tickets', '/reminders', '/measuring-instrument-inspectors', '/inspection-appointments'],
    note: 'P.K.M. Uzmanı ana dizinden kendi genel görünümüne yönlendirilir. Bu ekrandaki Planların Cihaz İhtiyaçları kartı yönetici ve merkez kullanıcısına açıktır.',
    menuVisible: true,
  },
  {
    id: 'inspector-dashboard',
    title: 'P.K.M. Uzmanı Genel Görünüm',
    path: '/inspector',
    roles: ['INSPECTOR'],
    apiGroups: ['/inspection-appointments', '/documents', '/reminders'],
    note: 'Planların Cihaz İhtiyaçları kartı oturumdaki uzmanın planlarını ve onaylı cihaz atamalarını karşılaştırır. Kişisel device-readiness endpoint’i kullanılır; drawer uzman sütunu içermez.',
    menuVisible: true,
  },
  {
    id: 'inspector-plan',
    title: 'Görevlerim',
    path: '/inspector/plan',
    roles: ['INSPECTOR'],
    apiGroups: ['/inspection-appointments', '/leaves', '/public-holidays', '/companies', '/proposals'],
    menuVisible: true,
  },
  {
    id: 'inspector-external-documents',
    title: 'P.K.M. Dış Kaynaklı Dokümanlar',
    path: '/inspector/external-documents',
    roles: ['INSPECTOR'],
    apiGroups: ['/external-documents'],
    menuVisible: true,
  },
  {
    id: 'inspector-nonconformity-reasons',
    title: 'P.K.M. Uygunsuzluk Nedenleri',
    path: '/inspector/nonconformity-reasons',
    roles: ['INSPECTOR'],
    apiGroups: ['/nonconformity-reasons', '/attributes', '/tools'],
    menuVisible: true,
  },
  {
    id: 'profile',
    title: 'Hesap Bilgileri',
    path: '/profile',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR', 'CUSTOMER'],
    apiGroups: ['/accounts/me', '/customers/me', '/users/me', '/account-users', '/images', '/documents', '/agreements'],
    menuVisible: true,
  },
  {
    id: 'proposals',
    title: 'Teklif Yönetimi',
    path: '/proposals',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'CUSTOMER'],
    apiGroups: ['/proposals', '/companies', '/attributes', '/authorized-persons', '/negative-proposal-reasons', '/reminders', '/documents', '/statuses', '/tools'],
    note: 'İş Ortağı rolünde müşteri teklif listesi, iç rollerde operasyon teklif listesi açılır.',
    menuVisible: true,
  },
  {
    id: 'proposal-create',
    title: 'Teklif Oluştur',
    path: '/proposals/create',
    roles: ['ADMIN', 'CENTRAL_USER', 'CUSTOMER'],
    apiGroups: ['/proposals', '/companies', '/attributes', '/authorized-persons', '/address-properties', '/images', '/settings', '/tools'],
    menuVisible: false,
  },
  {
    id: 'proposal-report',
    title: 'Rapor Detayları',
    path: '/proposals/report',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/proposals', '/companies', '/attributes', '/authorized-persons', '/address-properties', '/images', '/settings', '/tools'],
    note: 'Teklif ve iş takip akışlarından açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'mailing',
    title: 'Mailing Yönetimi',
    path: '/mailing/:tab?',
    roles: ['ADMIN'],
    apiGroups: ['/mailing'],
    note: 'Route seviyesinde ADMIN kontrolü vardır.',
    menuVisible: false,
  },
  {
    id: 'agreement-create',
    title: 'Sözleşme Oluştur',
    path: '/agreements/create',
    roles: ['ADMIN'],
    apiGroups: ['/agreements'],
    menuVisible: false,
  },
  {
    id: 'companies',
    title: 'Firmalar',
    path: '/companies',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'CUSTOMER'],
    apiGroups: ['/companies', '/authorized-persons', '/address-properties', '/images', '/reminders', '/users'],
    menuVisible: true,
  },
  {
    id: 'company-nonconformities',
    title: 'Firma Uygunsuzlukları',
    path: '/companies/:id/nonconformities',
    roles: ['ADMIN', 'CENTRAL_USER', 'INSPECTOR'],
    apiGroups: ['/companies', '/documents', '/proposals'],
    note: 'Firma detayından açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'company-control-reports',
    title: 'Firma Kontrol Raporları',
    path: '/companies/:id/control-reports',
    roles: ['ADMIN', 'CENTRAL_USER', 'INSPECTOR'],
    apiGroups: ['/companies', '/control', '/proposals'],
    note: 'Firma detayından açılan kontrol raporu liste route.',
    menuVisible: false,
  },
  {
    id: 'company-detail',
    title: 'Firma Detayı',
    path: '/companies/:id',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'CUSTOMER'],
    apiGroups: ['/companies', '/authorized-persons', '/company-evaluations', '/notes', '/proposals', '/reminders', '/documents', '/settings'],
    note: 'Firma listesinden açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'user-detail',
    title: 'Kullanıcı Detayı',
    path: '/users/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/users', '/roles', '/customers', '/accounts', '/companies', '/proposals', '/documents'],
    permission: 'users.show',
    note: 'Kullanıcı listesinden açılan detay route. Route seviyesinde users.show izni kontrol edilir.',
    menuVisible: false,
  },
  {
    id: 'wallet',
    title: 'Cüzdan',
    path: '/wallet',
    roles: ['CUSTOMER'],
    apiGroups: ['/companies', '/proposals', '/documents', '/users'],
    menuVisible: true,
  },
  {
    id: 'notes',
    title: 'Notlar',
    path: '/notes',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/notes', '/accounts/me'],
    menuVisible: false,
  },
  {
    id: 'tickets',
    title: 'Destek Taleplerim',
    path: '/tickets',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR', 'CUSTOMER'],
    apiGroups: ['/tickets', '/accounts/me/tickets', '/ticket-responses', '/ticket-attachments'],
    note: 'ticket.show izni yoksa destek menüsü bu sayfaya yönlenir.',
    menuVisible: true,
  },
  {
    id: 'all-tickets',
    title: 'Tüm Destek Talepleri',
    path: '/all-tickets',
    roles: ['ADMIN', 'CENTRAL_USER', 'INSPECTOR'],
    apiGroups: ['/tickets', '/accounts/me/tickets', '/ticket-responses', '/ticket-attachments', '/users'],
    permission: 'ticket.show',
    menuVisible: true,
  },
  {
    id: 'statuses',
    title: 'Durumlar',
    path: '/statuses',
    roles: ['ADMIN'],
    apiGroups: ['/statuses'],
    menuVisible: false,
  },
  {
    id: 'settings',
    title: 'Ayarlar',
    path: '/settings',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/roles', '/permissions', '/settings', '/statuses', '/agreements', '/announcements', '/attributes', '/answers', '/questions', '/report-formats', '/report-nodes'],
    permission: 'settings.show',
    menuVisible: true,
  },
  {
    id: 'settings-report-status',
    title: 'Rapor Format Durumu',
    path: '/settings/parameters/reports',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-formats'],
    permission: 'settings.show',
    menuVisible: false,
  },
  {
    id: 'settings-tab',
    title: 'Ayarlar Alt Sayfası',
    path: '/settings/:tab',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/roles', '/permissions', '/settings', '/statuses', '/agreements', '/announcements', '/attributes', '/answers', '/questions', '/report-formats', '/report-nodes', '/inspectors', '/professions', '/public-holidays', '/measuring-instruments'],
    permission: 'settings.show',
    note: 'Alt sayfaya göre API grubu değişir. access-inventory, states ve districts ayrıca ADMIN kontrolüyle korunur.',
    menuVisible: true,
  },
  {
    id: 'all-notifications',
    title: 'Duyurular',
    path: '/all-notifications',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR', 'CUSTOMER'],
    apiGroups: ['/announcements/notifications', '/notifications'],
    menuVisible: false,
  },
  {
    id: 'project-followup',
    title: 'İş Yönetimi',
    path: '/project-followup',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE'],
    apiGroups: ['/proposals', '/inspection-appointments', '/control', '/companies', '/users', '/attributes', '/report-nodes', '/reminders'],
    menuVisible: true,
  },
  {
    id: 'reports',
    title: 'Raporlar',
    path: '/reports',
    roles: ['ADMIN'],
    apiGroups: ['/proposals/report', '/companies/report', '/customers/report', '/partner-report', '/documents', '/statuses'],
    permission: 'report.show',
    note: 'Route ve menü seviyesinde ADMIN rolü ayrıca kontrol edilir.',
    menuVisible: true,
  },
  {
    id: 'opportunity',
    title: 'Ajanda',
    path: '/opportunity',
    roles: ['ADMIN', 'CENTRAL_USER', 'CUSTOMER'],
    apiGroups: ['/proposals'],
    menuVisible: true,
  },
  {
    id: 'payments',
    title: 'Ödemeler',
    path: '/payments',
    roles: ['ADMIN', 'FINANCE'],
    apiGroups: ['/proposals', '/inspection-appointments', '/control', '/companies', '/users', '/reminders', '/documents'],
    menuVisible: true,
  },
  {
    id: 'roles',
    title: 'Roller',
    path: '/roles',
    roles: ['ADMIN'],
    apiGroups: ['/roles', '/permissions'],
    permission: 'roles.list',
    menuVisible: false,
  },
  {
    id: 'permissions',
    title: 'Yetkiler',
    path: '/permissions',
    roles: ['SA'],
    apiGroups: ['/permissions'],
    note: 'Yalnızca SA rolündeki kullanıcılar doğrudan URL ile erişebilir; menüde gösterilmez.',
    menuVisible: false,
  },
  {
    id: 'users',
    title: 'Kullanıcılar',
    path: '/users',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/users', '/roles', '/customers'],
    permission: 'users.show',
    note: 'Route ve menü seviyesinde users.show izni kontrol edilir.',
    menuVisible: true,
  },
  {
    id: 'professions',
    title: 'Meslekler',
    path: '/professions',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/professions', '/measuring-instruments', '/attributes'],
    menuVisible: false,
  },
  {
    id: 'measuring-instruments',
    title: 'Ölçüm Cihazları',
    path: '/measuring-instruments',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/measuring-instruments', '/attributes'],
    menuVisible: false,
  },
  {
    id: 'public-holidays',
    title: 'Resmi Tatiller',
    path: '/public-holidays',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/public-holidays'],
    permission: 'settings.show',
    menuVisible: false,
  },
  {
    id: 'measuring-instrument-inventories',
    title: 'Ölçüm Cihazı Envanteri',
    path: '/measuring-instrument-inventories',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/measuring-instrument-inventories', '/measuring-instruments', '/inspectors', '/documents'],
    menuVisible: false,
  },
  {
    id: 'measuring-instrument-inventory-detail',
    title: 'Ölçüm Cihazı Envanteri Detayı',
    path: '/measuring-instrument-inventories/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/measuring-instrument-inventories', '/measuring-instruments', '/measuring-instrument-calibrations', '/measuring-instrument-inspectors', '/inspectors', '/documents'],
    note: 'Envanter listesinden açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'measuring-instrument-calibrations',
    title: 'Cihaz Kalibrasyonları',
    path: '/measuring-instrument-calibrations',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/measuring-instrument-calibrations', '/documents'],
    menuVisible: false,
  },
  {
    id: 'measuring-instrument-inspectors',
    title: 'Cihaz Denetçileri',
    path: '/measuring-instrument-inspectors',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/measuring-instrument-inspectors', '/measuring-instrument-inventories', '/inspectors', '/documents'],
    menuVisible: false,
  },
  {
    id: 'inspectors',
    title: 'P.K.M. Uzmanları',
    path: '/inspectors',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspectors', '/professions', '/states', '/countries'],
    menuVisible: false,
  },
  {
    id: 'inspector-detail',
    title: 'P.K.M. Uzman Detayı',
    path: '/inspectors/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspectors', '/inspector-professions', '/leaves', '/measuring-instrument-inventories', '/professions'],
    note: 'P.K.M. uzman listesinden açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'inspector-professions',
    title: 'P.K.M. Meslekleri',
    path: '/inspector-professions',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspector-professions', '/professions'],
    menuVisible: false,
  },
  {
    id: 'leaves',
    title: 'İzinler',
    path: '/leaves',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/leaves'],
    menuVisible: false,
  },
  {
    id: 'external-documents',
    title: 'Dış Kaynaklı Dokümanlar',
    path: '/external-documents',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/external-documents', '/attributes', '/control-areas', '/documents'],
    menuVisible: false,
  },
  {
    id: 'report-formats',
    title: 'Rapor Formatları',
    path: '/report-formats',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-formats', '/attributes', '/images'],
    menuVisible: false,
  },
  {
    id: 'report-format-detail',
    title: 'Rapor Formatı Detayı',
    path: '/report-formats/detail/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-formats', '/report-nodes', '/questions', '/answers', '/attributes', '/tools', '/measuring-instruments'],
    note: 'Rapor formatı listesinden açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'report-formats-by-attribute',
    title: 'Rapor Formatı Listesi',
    path: '/report-formats/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-formats', '/attributes', '/images'],
    note: "Parametreye göre filtrelenen rapor formatı route'u.",
    menuVisible: false,
  },
  {
    id: 'report-nodes',
    title: 'Rapor Yapısı',
    path: '/report-nodes',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-nodes', '/report-formats', '/questions', '/answers', '/attributes', '/tools'],
    menuVisible: false,
  },
  {
    id: 'report-node-viewer',
    title: 'Rapor Yapısı Önizleme',
    path: '/report-nodes/view/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/report-nodes', '/questions', '/answers'],
    note: "Rapor yapısı detay önizleme route'u.",
    menuVisible: false,
  },
  {
    id: 'report-node-viewer-all',
    title: 'Rapor Önizle',
    path: '/report-nodes/view-all/:reportFormatUuid',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/field-inspections', '/control-areas'],
    note: "Rapor formatı genel önizleme route'u.",
    menuVisible: false,
  },
  {
    id: 'questions',
    title: 'Sorular',
    path: '/questions',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/questions', '/answers', '/attributes', '/report-formats', '/report-nodes', '/tools'],
    menuVisible: false,
  },
  {
    id: 'answers',
    title: 'Cevaplar',
    path: '/answers',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/answers', '/questions', '/report-formats'],
    menuVisible: false,
  },
  {
    id: 'inspection-appointments',
    title: 'Denetim Randevuları',
    path: '/inspection-appointments',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspection-appointments', '/authorized-persons', '/companies', '/proposals'],
    menuVisible: false,
  },
  {
    id: 'inspection-appointment-detail',
    title: 'Denetim Randevusu Detayı',
    path: '/inspection-appointments/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspection-appointments', '/inspection-tasks', '/inspectors', '/measuring-instrument-inventories', '/proposals'],
    note: 'Denetim randevusu listesinden açılan detay route.',
    menuVisible: false,
  },
  {
    id: 'inspection-tasks',
    title: 'Denetim Görevleri',
    path: '/inspection-tasks',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspection-tasks', '/inspectors', '/measuring-instrument-inventories', '/attributes'],
    menuVisible: false,
  },
  {
    id: 'planning-detail',
    title: 'Planla',
    path: '/planning/detail/:id',
    roles: ['ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/inspection-appointments', '/planning-reservations', '/public-holidays', '/proposals', '/companies', '/groups'],
    note: 'İş yönetimi ve teklif akışlarından açılan planlama detayı.',
    menuVisible: false,
  },
  {
    id: 'inspector-devices',
    title: 'Cihazlarım',
    path: '/inspector/devices',
    roles: ['INSPECTOR'],
    apiGroups: ['/measuring-instrument-inventories', '/measuring-instrument-inspectors'],
    menuVisible: true,
  },
  {
    id: 'inspector-professions-me',
    title: 'Mesleklerim',
    path: '/inspector/professions',
    roles: ['INSPECTOR'],
    apiGroups: ['/inspector-professions'],
    menuVisible: false,
  },
  {
    id: 'inspector-planning-detail',
    title: 'Periyodik Kontrol Planı',
    path: '/inspector/planning/detail/:id',
    roles: ['INSPECTOR'],
    apiGroups: ['/inspection-appointments', '/inspection-tasks', '/inspectors', '/proposals', '/control', '/documents', '/report-formats'],
    note: "P.K.M. uzmanının görev detay route'u.",
    menuVisible: false,
  },
  {
    id: 'control-task',
    title: 'Periyodik Kontrol Formu',
    path: '/control/:proposalId/taskId/:taskId/attribute/:attributeId/order/:order',
    roles: ['INSPECTOR', 'ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/control', '/field-inspections', '/inspection-tasks', '/nonconformity-reasons', '/proposals', '/documents'],
    note: "Saha kontrol formu route'u.",
    menuVisible: false,
  },
  {
    id: 'control-task-report',
    title: 'Periyodik Kontrol Raporu',
    path: '/control/:proposalId/taskId/:taskId/attribute/:attributeId/order/:order/report',
    roles: ['INSPECTOR', 'ADMIN', 'CENTRAL_USER'],
    apiGroups: ['/control', '/field-inspections', '/inspection-tasks', '/nonconformity-reasons', '/proposals', '/documents'],
    note: "Saha kontrol raporu route'u.",
    menuVisible: false,
  },
  {
    id: 'reminders',
    title: 'Görevler',
    path: '/reminders',
    roles: ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR'],
    apiGroups: ['/reminders', '/proposals', '/companies', '/statuses', '/documents', '/users'],
    menuVisible: true,
  },
  {
    id: 'login',
    title: 'Giriş',
    path: '/login',
    roles: ['PUBLIC'],
    apiGroups: ['/auth/login', '/oauth2/redirect-uri', '/customers/me', '/users/me', '/images'],
    note: "Auth layout route'u.",
  },
  {
    id: 'register',
    title: 'Kayıt',
    path: '/register',
    roles: ['PUBLIC'],
    apiGroups: ['/agreements', '/auth/register'],
    note: "Auth layout route'u.",
  },
  {
    id: 'forgot-password',
    title: 'Şifremi Unuttum',
    path: '/forgot-password',
    roles: ['PUBLIC'],
    apiGroups: ['/auth/reset-password-requests'],
    note: "Auth layout route'u.",
  },
  {
    id: 'forgot-password-confirm',
    title: 'Yeni Şifre Oluştur',
    path: '/forgot-password-confirm',
    roles: ['PUBLIC'],
    apiGroups: ['/auth/reset-password'],
    note: "Auth layout route'u.",
  },
  {
    id: 'email-verify-check',
    title: 'Aktivasyon E-postası Gönder',
    path: '/email-verify-check',
    roles: ['PUBLIC'],
    apiGroups: ['/auth/verification-send'],
    note: "Auth layout route'u.",
  },
  {
    id: 'oauth-google',
    title: 'Google OAuth Dönüşü',
    path: '/oauth2/google/redirect-uri',
    roles: ['PUBLIC'],
    apiGroups: ['/oauth2/token', '/customers/me', '/images'],
    note: "OAuth sağlayıcı dönüş route'u.",
  },
  {
    id: 'oauth-linkedin',
    title: 'LinkedIn OAuth Dönüşü',
    path: '/oauth2/linkedin/redirect-uri',
    roles: ['PUBLIC'],
    apiGroups: ['/oauth2/token', '/customers/me', '/images'],
    note: "OAuth sağlayıcı dönüş route'u.",
  },
  {
    id: 'email-verification',
    title: 'E-posta Doğrulama',
    path: '/email-verification',
    roles: ['PUBLIC'],
    apiGroups: ['/auth/email-verification'],
  },
  {
    id: 'email-change-verification',
    title: 'E-posta Değişikliği Doğrulama',
    path: '/email-change-verification',
    roles: ['AUTH'],
    apiGroups: ['/customers/me/email-update-verify'],
  },
  {
    id: 'negative-reason-survey',
    title: 'Olumsuz Teklif Anketi',
    path: '/public/negative-reason-survey',
    roles: ['PUBLIC'],
    apiGroups: ['/negative-proposal-reasons/public'],
  },
  {
    id: 'mailing-survey',
    title: 'Mailing Anketi',
    path: '/public/mailing-survey',
    roles: ['PUBLIC'],
    apiGroups: ['/mailing/public/surveys'],
  },
  {
    id: 'report-verification',
    title: 'Rapor Doğrulama',
    path: '/report/verify/:id',
    roles: ['PUBLIC'],
    apiGroups: ['/control/public/report-verifications'],
  },
  {
    id: 'secret-gallery',
    title: 'Gizli Galeri',
    path: '/secret-gallery',
    roles: ['AUTH'],
    apiGroups: [],
    note: 'Teknik/gizli route. API çağrısı yok.',
  },
  {
    id: 'proposal-preview',
    title: 'Teklif Önizleme',
    path: '/proposals/preview/:proposalId',
    roles: ['AUTH'],
    apiGroups: ['/proposals', '/attributes', '/settings'],
    note: "Teklif PDF önizleme route'u.",
  },
];
